import { oc } from "@orpc/contract";
import { z } from "zod";
import { ShipAndDebitEntityConfigSchema } from "@erp/shared-types";

const entityModuleConfigResponseSchema = z.object({
  entityId: z.uuid(),
  entityType: z.string(),
  module: z.string(),
  config: ShipAndDebitEntityConfigSchema,
});

const getEntityModuleConfigContract = oc
  .input(
    z.object({
      entityId: z.uuid(),
      entityType: z.string().min(1),
      module: z.string().min(1),
    }),
  )
  .output(entityModuleConfigResponseSchema)
  .route({
    method: "GET",
    summary: "Get per-entity module configuration",
    description:
      "Returns one company's settings for a module, such as ship-and-debit supplier configuration; empty when none is saved.",
  });

const updateEntityModuleConfigContract = oc
  .input(
    z.object({
      entityId: z.uuid(),
      entityType: z.string().min(1),
      module: z.string().min(1),
      config: ShipAndDebitEntityConfigSchema,
    }),
  )
  .output(entityModuleConfigResponseSchema)
  .route({
    summary: "Update per-entity module configuration",
    description:
      "Replaces one company's settings for a module, such as ship-and-debit supplier configuration, and returns the saved settings.",
  });

const listEntityModuleConfigResponseSchema =
  entityModuleConfigResponseSchema.extend({
    entityName: z.string().nullable().optional(),
  });

const listEntityModuleConfigsContract = oc
  .input(
    z.object({
      module: z.string().min(1),
      entityType: z.string().min(1),
    }),
  )
  .output(z.object({ configs: z.array(listEntityModuleConfigResponseSchema) }))
  .route({
    method: "GET",
    summary: "List all entity module configurations for a module",
    description:
      "Returns every company in the organization that has settings for the given module, with company name and configuration. Only companies are supported.",
  });

const submissionRuleSchema = z.object({
  name: z.string().min(1),
  type: z.enum(["include", "exclude"]).default("exclude"),
  pipeline: z.enum(["batch", "vdm", "all"]).default("all"),
  logic: z.enum(["and", "or"]).default("and"),
  conditions: z.array(
    z.object({
      field: z.string().min(1),
      operator: z.string().min(1),
      value: z.unknown().optional(),
    }),
  ),
});

const bulkUpdateSubmissionRulesContract = oc
  .input(
    z.object({
      rules: z.array(
        z.object({
          rule: submissionRuleSchema,
          entityIds: z.array(z.uuid()),
        }),
      ),
    }),
  )
  .output(z.object({ updated: z.number() }))
  .route({
    summary:
      "Bulk update submission validation rules across multiple suppliers",
    description:
      "Replaces ship-and-debit claim submission rules for all suppliers: each listed supplier gets the given rules, every other supplier's rules are cleared. Returns count changed.",
  });

export const entityModuleConfigContracts = {
  get: getEntityModuleConfigContract,
  update: updateEntityModuleConfigContract,
  list: listEntityModuleConfigsContract,
  bulkUpdateSubmissionRules: bulkUpdateSubmissionRulesContract,
};
